/// <amd-module name="TFS/FeatureManagement/Components/FeatureBanner/FeatureBanner" />
import * as React from "react";
import { ContributedFeature, ContributedFeatureEnabledValue, ContributedFeatureState } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import * as FeatureResources from "TFS/FeatureManagement/Resources";
import { ITfsFeatureService } from "TFS/FeatureManagement/Types";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format as stringFormat } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Link } from "VSSUI/Link";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { IFeatureAdsService } from "../../FeatureAdsService";
import { IFeaturePanel } from "../../FeaturePanelService";

interface IFeatureBannerComponentProps {
    feature: ContributedFeature;
    message: string;
    showDismissCallout?: boolean;
    onDismiss: () => void;
}

const telemetryArea = "FeatureBanner";

class FeatureBannerComponent extends VssComponent<IFeatureBannerComponentProps, any> {
    public static componentType = "featureGlobalMessageBanner";

    private featureService: ITfsFeatureService;
    private reloadOnFeatureSet: boolean;

    constructor(props: IFeatureBannerComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};

        this.featureService = context.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");
        this.featureService.subscribe(this.onFeatureStateChanged, "featureStateUpdated");
    }

    public render(): JSX.Element {
        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    const mobile = screenSizeProps.screenSize <= ScreenSize.xsmall;
                    return (
                        <div className={mobile ? "flex-column rhythm-vertical-16" : "flex-row flex-center rhythm-horizontal-16"}>
                            <div className="rhythm-horizontal-4">
                                <span className="font-weight-semibold">
                                    {stringFormat(FeatureResources.FeatureBannerNameFormat, this.props.feature.name)}
                                </span>
                                <span>{this.props.message || this.props.feature.description || ""}</span>
                                {this.props.feature._links && this.props.feature._links.learn ? (
                                    <Link href={this.props.feature._links.learn.href} className="learn-more flex-noshrink" target="_blank">
                                        {FeatureResources.LearnMore}
                                    </Link>
                                ) : null}
                            </div>
                            <ButtonGroup className="flex-noshrink">
                                <Button onClick={this.onDismissedClicked}>{FeatureResources.FeatureBannerDismissButton}</Button>
                                <Button primary={true} onClick={this.onTryItClicked}>
                                    {FeatureResources.FeatureBannerTryItButton}
                                </Button>
                            </ButtonGroup>
                        </div>
                    );
                }}
            </ScreenSizeObserver>
        );
    }

    public componentWillUnmount() {
        this.featureService.unsubscribe(this.onFeatureStateChanged, "featureStateUpdated");

        super.componentWillUnmount();
    }

    private onFeatureStateChanged = (featureState: ContributedFeatureState): void => {
        if (featureState.featureId === this.props.feature.id) {
            this.dismissAd().then(() => {
                const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
                featureAdService.prepareShowFeatureEnabledCallout(this.props.feature.id);

                // Reload the page
                if (this.reloadOnFeatureSet) {
                    window.location.reload();
                }
            });
        }
    };

    private onTryItClicked = (): void => {
        this.reloadOnFeatureSet = true;

        const userScoped = this.props.feature.scopes.filter(s => s.userScoped).length > 0;

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(telemetryArea, "bannerTryItClicked", {
            featureId: this.props.feature.id,
            openedPanel: !userScoped
        });

        if (userScoped) {
            // User-scoped feature. Toggle the scope on for the user right here through an XHR call
            const stateToUpdate = {
                state: ContributedFeatureEnabledValue.Enabled
            } as ContributedFeatureState;

            this.featureService.setFeatureState(this.props.feature, true, undefined, undefined, ContributedFeatureEnabledValue.Enabled);
        } else {
            const featurePanelService = this.context.pageContext.getService<IFeaturePanel>("IFeaturePanel");
            featurePanelService.show(this.props.feature.scopes[0]);
        }
    };

    private onDismissedClicked = (): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(telemetryArea, "bannerDismissed", {
            featureId: this.props.feature.id
        });

        this.props.onDismiss();
        this.dismissAd();

        if (this.props.showDismissCallout) {
            const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
            featureAdService.showFeatureAdDismissedCallout();
        }
    };

    private dismissAd(): Promise<void> {
        const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
        return featureAdService.dismissFeatureAd(this.props.feature.id);
    }
}

VssComponent.register(FeatureBannerComponent.componentType, FeatureBannerComponent);
